import React, { useState } from 'react';
import { Play, Download, Box, Smartphone, Monitor, Clock, AlertCircle, CheckCircle, Loader2, RotateCw } from 'lucide-react';
import { useProjectStore } from '../../stores/projectStore';

export function BuildPanel({ className }: { className?: string }) {
  const { currentProject, buildProject, isBuilding } = useProjectStore();
  const [selectedPlatform, setSelectedPlatform] = useState('webgl');

  // Filter builds from project history
  const builds = currentProject?.builds || [];
  const latestBuild = builds[builds.length - 1];
  const isBuildInProgress = isBuilding || (latestBuild && latestBuild.status === 'building');

  const handleBuild = async () => {
    if (!currentProject) return;
    await buildProject(selectedPlatform);
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'webgl': return <Box size={16} />;
      case 'android': return <Smartphone size={16} />;
      case 'windows': return <Monitor size={16} />;
      default: return <Box size={16} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'building': return 'text-blue-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success': return <CheckCircle size={14} />;
      case 'failed': return <AlertCircle size={14} />;
      case 'building': return <Loader2 size={14} className="animate-spin" />;
      default: return <Clock size={14} />;
    }
  };

  return (
    <div className={`build-panel ${className || ''}`}>
      <div className="panel-header">
        <h3>Build & Release</h3>
      </div>
      
      <div className="panel-content build-content">
        {/* New Build Section */}
        <div className="build-section">
          <div className="section-title">Create New Build</div>
          
          <div className="platform-grid">
            <button 
              className={`platform-btn ${selectedPlatform === 'webgl' ? 'active' : ''}`}
              onClick={() => setSelectedPlatform('webgl')}
            >
              <Box size={24} />
              <span>WebGL</span>
            </button>
            <button 
              className={`platform-btn ${selectedPlatform === 'android' ? 'active' : ''}`}
              onClick={() => setSelectedPlatform('android')}
            >
              <Smartphone size={24} />
              <span>Android</span>
            </button>
            <button 
              className={`platform-btn ${selectedPlatform === 'windows' ? 'active' : ''}`}
              onClick={() => setSelectedPlatform('windows')}
            >
              <Monitor size={24} />
              <span>Windows</span>
            </button>
          </div>

          <div className="build-info">
            <p className="info-text">
              Target: <span className="text-white font-bold">{selectedPlatform.toUpperCase()}</span>
            </p>
            <p className="info-subtext">Estimated time: 10-15 minutes</p>
          </div>

          <button 
            className={`build-action-btn ${isBuildInProgress ? 'disabled' : ''}`}
            onClick={handleBuild}
            disabled={isBuildInProgress}
          >
            {isBuildInProgress ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Building in Cloud...
              </>
            ) : (
              <>
                <RotateCw size={18} />
                Start Build
              </>
            )}
          </button>
        </div>

        {/* Build History */}
        <div className="build-section flex-1 overflow-hidden flex flex-col">
          <div className="section-title mb-2">Build History</div>
          
          <div className="history-list">
            {builds.length === 0 ? (
              <div className="empty-history">
                <Box size={32} className="opacity-20 mb-2" />
                <p>No builds yet</p>
              </div>
            ) : (
              [...builds].reverse().map((build) => (
                <div key={build.id} className="build-item">
                  <div className="build-icon">
                    {getPlatformIcon(build.platform)}
                  </div>
                  
                  <div className="build-details">
                    <div className="build-meta">
                      <span className="build-platform">{build.platform}</span>
                      <span className={`build-status ${getStatusColor(build.status)}`}>
                        {getStatusIcon(build.status)}
                        {build.status}
                      </span>
                    </div>
                    <div className="build-time">
                      {new Date(build.createdAt).toLocaleString()}
                    </div>
                  </div>

                  <div className="build-actions">
                    {build.status === 'success' && build.url && (
                      build.platform === 'webgl' ? (
                        <button 
                          className="action-btn play"
                          onClick={() => window.open(build.url, '_blank')}
                          title="Play"
                        >
                          <Play size={14} />
                        </button>
                      ) : (
                        <button 
                          className="action-btn download"
                          onClick={() => window.open(build.url, '_blank')}
                          title="Download"
                        >
                          <Download size={14} />
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .build-panel {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #232323;
        }

        .build-content {
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 24px;
          height: 100%;
        }

        .section-title {
          font-size: 12px;
          font-weight: 600;
          color: #888;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 12px;
        }

        .platform-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          margin-bottom: 16px;
        }

        .platform-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: #2a2a2a;
          border: 1px solid #333;
          border-radius: 6px;
          color: #888;
          cursor: pointer;
          transition: all 0.2s;
        }

        .platform-btn:hover {
          background: #333;
          color: #ccc;
        }

        .platform-btn.active {
          background: #375eab;
          border-color: #375eab;
          color: white;
        }

        .build-info {
          background: #2a2a2a;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 16px;
          border-left: 3px solid #375eab;
        }

        .info-text {
          font-size: 13px;
          color: #ccc;
          margin: 0 0 4px 0;
        }

        .info-subtext {
          font-size: 11px;
          color: #666;
          margin: 0;
        }

        .build-action-btn {
          width: 100%;
          padding: 12px;
          background: #2ecc71;
          color: #000;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: filter 0.2s;
        }

        .build-action-btn:hover {
          filter: brightness(1.1);
        }

        .build-action-btn.disabled {
          background: #333;
          color: #666;
          cursor: not-allowed;
        }

        .history-list {
          flex: 1;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .empty-history {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: #444;
          font-size: 13px;
        }

        .build-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px;
          background: #2a2a2a;
          border-radius: 6px;
          border: 1px solid #333;
        }

        .build-icon {
          width: 32px;
          height: 32px;
          background: #222;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #666;
        }

        .build-details {
          flex: 1;
        }

        .build-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 2px;
        }

        .build-platform {
          font-weight: 600;
          color: #ccc;
          text-transform: capitalize;
        }

        .build-status {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          text-transform: uppercase;
        }

        .build-time {
          font-size: 11px;
          color: #666;
        }

        .build-actions {
          display: flex;
          gap: 4px;
        }

        .action-btn {
          width: 28px;
          height: 28px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          background: #333;
          color: #ccc;
          border: none;
          transition: all 0.2s;
        }

        .action-btn:hover {
          background: #444;
          color: white;
        }

        .action-btn.play:hover {
          background: #375eab;
        }

        .action-btn.download:hover {
          background: #2ecc71;
          color: black;
        }
      `}</style>
    </div>
  );
}
